/* eslint-disable max-lines */
import * as React from 'react';
import { action, extendObservable, observable, runInAction } from 'mobx';
import { Button, Card, CardBody, CardTitle, Col, Form, FormGroup, Input } from 'reactstrap';
import { observer } from 'mobx-react';

import { ApiUrls, DateTime, ParameterType } from '@app/AppConstants';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import {
    CronValidationResult,
    JobDetailsModel,
    JobMisfireInstruction,
    JobParameterDescriptionModel,
    JobParameterModel,
    JobParameterType,
    JobSaveParametersModel,
    JobTriggerShortInfo
} from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';
import { BaseFormModel } from '@app/Services/BaseFormModel';
import { DateTimeService } from '@app/Services/DateTimeService';
import { extensions } from '@app/Services/Extensions';
import { addDateTimeValidator, addDateValidator, addNumberValidator, addRequiredValidator, isDateTime, isRequiredWhen } from '@app/Services/Validation';

import { Icon } from '@app/Components/Icon';
import { FormCheckbox, FormInput, FormSimpleSelect, FormSwitcher } from '@app/Components/FormControls';
import { DatePickerValidatedControl } from '@app/Components/DateTimeControls/DateTimeControls';
import { RemoteSuggestInput } from '@app/Components/RemoteSuggestInput/RemoteSuggestInput';
import { RemoteOptionsStore } from '@app/Components/RemoteSuggestInput/RemoteOptionsStore';
import { IModalDialogContent, ModalButtonOptions, ModalButtonType, ModalDialogOptions, modalService, ModalWindow } from '@app/Components/Modal';
import './job-parameters-dialog.scss';

const CRON_FIELDS: Array<[string, string]> = [
    ['second', '0 - 59'],
    ['minute', '0 - 59'],
    ['hour', '0 - 23'],
    ['day (month)', '1 - 31'],
    ['month', '1 - 12, JAN-DEC'],
    ['day (week)', '0 - 6, SUN-SAT']
];

const CRON_SAMPLES: Array<[string, string]> = [
    ['Every 15 mins', '0 */15 * * * *'],
    ['Every 4 hours', '0 0 */4 * * *'],
    ['Every day at 10', '0 0 10 * * *'],
    ['Every monday at 11', '0 0 11 * * MON']
];

const CRON_SPECIAL_CHARS: Array<[string, string]> = [
    ['*', 'any value'],
    [',', 'value list separator'],
    ['-', 'range of values'],
    ['/', 'step values']
];

const JOB_INSTANCES = [
    { label: 'Azure', value: 'Azure' },
    { label: 'VM', value: 'VM' }
];

const JOB_MISFIRE_INSTRUCTIONS: string[] = Object.values(JobMisfireInstruction);

export type JobParametersSubmitModel = JobSaveParametersModel & { isJobSettings?: boolean };

type OnSubmitHandler = (type: ModalButtonType, details: JobParametersSubmitModel) => Promise<boolean>;

type FieldsValidityMessage = {
    triggerNameField: string,
    cronField: { errorMessage: string, explanation: string }
};

type JobParametersDialogProps = {
    jobData: JobDetailsModel;
    trigger?: JobTriggerShortInfo | null;
    isJobSettings?: boolean;
    showTriggerState: boolean;
    hasWriteAccess?: boolean;
    hasRunAccess?: boolean;
    onSubmit: OnSubmitHandler;
};

class FormModel extends BaseFormModel {
    @observable
    triggerName: string = '';

    @observable
    isActive: boolean = true;

    @observable
    @isRequiredWhen('needUseCron', true)
    cronSchedule: string = '';

    @observable
    @isDateTime()
    @isRequiredWhen('needUseCron', false)
    dateTimeSchedule: string = '';

    @observable
    useZrhTimeZone: boolean = false;

    @observable
    misfireLogic: JobMisfireInstruction = JobMisfireInstruction.Default;

    @observable
    instance?: string;

    @observable
    needUseCron?: boolean;

    [key: string]: unknown;

    constructor(parameters?: JobParameterDescriptionModel[]) {
        super();
        parameters?.forEach((jobParameter: JobParameterDescriptionModel) => {
            if (!jobParameter.name) return;

            extendObservable(this, {
                [jobParameter.name]: ''
            });
            if (jobParameter.isRequired) addRequiredValidator(this, jobParameter.name, '', jobParameter.displayName);
            switch (jobParameter.type) {
                case ParameterType.DateTime:
                    addDateTimeValidator(this, jobParameter.name, '', jobParameter.displayName);
                    break;
                case ParameterType.Date:
                    addDateValidator(this, jobParameter.name, '', jobParameter.displayName);
                    break;
                case ParameterType.Integer:
                    addNumberValidator(this, jobParameter.name, '', jobParameter.displayName);
                    break;
            }
        });
    }
}

@observer
export class JobParametersDialog extends React.Component<JobParametersDialogProps> implements IModalDialogContent<void> {
    private readonly _store: JobParametersDialogStore;
    private _jobNameSpan: React.RefObject<HTMLSpanElement> = React.createRef();
    private _jobNameInput: React.RefObject<HTMLInputElement> = React.createRef();
    private readonly _isEditable: boolean;
    private readonly _isRunnable: boolean;

    constructor(props: JobParametersDialogProps) {
        super(props);
        this._store = new JobParametersDialogStore(props.showTriggerState, props.jobData, props.onSubmit, props.trigger);
        this._isEditable = !!props.hasWriteAccess;
        this._isRunnable = !!props.hasRunAccess;
    }

    private _getButtons(): ModalButtonOptions<void>[] {
        const { showTriggerState, isJobSettings } = this.props;

        if (isJobSettings) {
            return [{
                title: 'Save',
                color: 'success',
                type: ModalButtonType.Save,
                isDisabled: !this._isEditable,
                onClick: async () => await this._store.submit(ModalButtonType.Save, isJobSettings)
            }];
        }

        if (!showTriggerState || (!this._isEditable && this._isRunnable)) {
            return [
                {
                    title: 'Run',
                    color: 'success',
                    type: ModalButtonType.RunNow,
                    isDisabled: !this._isRunnable,
                    onClick: async () => await this._store.submit(ModalButtonType.RunNow)
                }
            ];
        }
        if (!this._store.trigger && showTriggerState) {
            return [
                {
                    title: 'Create',
                    color: 'success',
                    type: ModalButtonType.CreateNew,
                    isDisabled: !this._isEditable || !this._store.form.triggerName || !!this._store.validityMessage.triggerNameField,
                    onClick: async () => await this._store.submit(ModalButtonType.CreateNew)
                }
            ];
        }

        return [
            {
                title: 'Save',
                color: 'success',
                type: ModalButtonType.CreateNew,
                isDisabled: !this._isEditable,
                onClick: async () => await this._store.submit(ModalButtonType.Save),
                children: [
                    {
                        title: 'Run now',
                        type: ModalButtonType.RunNow,
                        color: 'success',
                        isDisabled: !this._isRunnable,
                        onClick: async () => await this._store.submit(ModalButtonType.RunNow)
                    },
                    {
                        title: 'Save and run now',
                        type: ModalButtonType.SaveAndRunNow,
                        color: 'success',
                        isDisabled: !this._isEditable,
                        onClick: async () => await this._store.submit(ModalButtonType.SaveAndRunNow)
                    }
                ]
            }
        ];
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        this._store.window = window;

        const buttons: ModalButtonOptions<void>[] = [];

        buttons.push({
            type: ModalButtonType.Cancel,
            onClick: () => {
                window.close();
            }
        });
        buttons.push(...this._getButtons());

        if (this._store.trigger) {
            buttons.push({
                type: ModalButtonType.Delete,
                color: 'danger',
                alignLeft: true,
                isDisabled: !this._isEditable,
                onClick: async () => {
                    const res = await this._store.submit(ModalButtonType.Delete);
                    if (res) {
                        window.close(ModalButtonType.Delete);
                    }
                }
            });
        }

        return {
            width: 700,
            buttons: buttons,
            loader: this._store.loader
        };
    }

    componentDidMount() {
        const span = this._jobNameSpan.current as Element;
        const input = this._jobNameInput.current;
        if (input) {
            input.style.paddingLeft = span.clientWidth.toString() + 'px';
        }
    }

    render() {
        const { showTriggerState, isJobSettings } = this.props;
        return (
            <div className="job-parameters-dialog">
                <Form onSubmit={(e) => e.preventDefault()}>
                    {!this._store.trigger && showTriggerState && (
                        <FormGroup row key="field_triggerName">
                            <Col sm={4}>Name</Col>
                            <Col sm={8}>
                                <span className="job-name" ref={this._jobNameSpan}>
                                    {this._store.jobTriggerPrefix}
                                </span>
                                <FormInput formModel={this._store.form} name="triggerName" innerRef={this._jobNameInput}
                                    invalid={!!this._store.validityMessage.triggerNameField} onChange={this._onChangeName} disabled={!this._isEditable} bsSize="sm" />
                                {!!this._store.validityMessage.triggerNameField && (
                                    <div className="job-validation-error">{this._store.validityMessage.triggerNameField}</div>
                                )}
                            </Col>
                        </FormGroup>
                    )}
                    {(showTriggerState || isJobSettings) && this._renderParametersCard()}
                    {this._store.parameters?.map(this._controlRenderer)}
                </Form>
                {this._renderTriggerInfo()}
            </div>
        );
    }

    @action.bound
    private _onChangeName(event: React.ChangeEvent<HTMLInputElement>) {
        const newValue = event.target.value;
        this._store.form.triggerName = newValue;

        const triggerName = this._store.jobTriggerPrefix + newValue;

        extensions.executeTimeout(this._checkValidity, 200, this, triggerName);
    }

    private async _checkValidity(triggerName: string) {
        const params = {
            triggerName: triggerName
        };
        const { data: result } = await ApiService.get<boolean>(ApiUrls.CheckTriggerNameUrl, params);
        runInAction(() => {
            this._store.validityMessage.triggerNameField = result === true ? '' : 'Name is not unique';
        });
    }

    private _controlRenderer = (jobParameter: JobParameterDescriptionModel) => {
        let control: JSX.Element;
        const isLocked = jobParameter.isOverrule === false;
        const isOverrule = jobParameter.isOverrule === true;
        const isDisabled = isLocked || !this._isEditable;
        const parameterName = jobParameter.name as keyof FormModel;
        switch (jobParameter.type) {
            case ParameterType.DateTime:
                control = <DatePickerValidatedControl pickerMode="datetime" formModel={this._store.form} name={parameterName} disabled={isDisabled} />;
                break;
            case ParameterType.Date:
                control = <DatePickerValidatedControl pickerMode="date" formModel={this._store.form} name={parameterName} disabled={isDisabled} />;
                break;
            case ParameterType.Boolean:
                control = <FormCheckbox label={jobParameter.displayName} formModel={this._store.form} name={parameterName} checked={false} disabled={isDisabled} />;
                break;
            case ParameterType.MultiLine:
                control = <FormInput formModel={this._store.form} name={parameterName} type="textarea" disabled={isDisabled} bsSize="sm" />;
                break;
            case ParameterType.Enum: {
                const options = (jobParameter.enumValues || []).map(x => ({ label: x.label ?? '', value: x.value ?? '' }));
                control = <FormSimpleSelect formModel={this._store.form} name={parameterName} options={options} disabled={isDisabled} />;
                break;
            }
            case ParameterType.CustomDropdown:
                control = (
                    <RemoteSuggestInput
                        formModel={this._store.form}
                        name={parameterName}
                        optionsUrl={jobParameter.optionsUrl}
                        formNamePrefix={this.props.jobData.name}
                        disabled={isDisabled}
                    />
                );
                break;
            default:
                control = <FormInput formModel={this._store.form} name={parameterName} formNamePrefix={this.props.jobData.name} autoComplete="on" disabled={isDisabled} bsSize="sm" />;
                break;
        }
        return (
            <FormGroup row key={'field_' + jobParameter.name}>
                <Col sm={4}>
                    {jobParameter.type === ParameterType.Boolean ? '' : jobParameter.displayName}
                    {!!jobParameter.description && <>&nbsp;<Icon name="question-circle" title={jobParameter.description} /></>}
                </Col>

                <Col sm={{ offset: 1, size: 7 }}>
                    <div className="position-relative">
                        <div className="d-flex align-items-center h-100" style={{ position: 'absolute', left: '-25px' }}>
                            {isLocked && <Icon name="lock" title={`Click to enable default value overrule. Default value: ${jobParameter.jobValueRaw}`} onClick={() => this._isEditable && this._enableDefaultValueOverrule(jobParameter)} style={{ fontSize: 12, cursor: this._isEditable ? 'pointer' : 'default', opacity: this._isEditable ? 1 : 0.5 }} />}
                            {isOverrule && <Icon name="lock-open" title={`Click to disable default value overrule. Default value: ${jobParameter.jobValueRaw}`} onClick={() => this._isEditable && this._disableDefaultValueOverrule(jobParameter)} style={{ fontSize: 12, cursor: this._isEditable ? 'pointer' : 'default', opacity: this._isEditable ? 1 : 0.5 }} />}
                        </div>
                        {control}
                    </div>
                </Col>
            </FormGroup>
        );
    };

    private _renderTriggerInfo() {
        if (!this._store.trigger) return '';
        const { lastRunDateTime, nextRunDateTime } = this._store.trigger;
        const lastRun = (lastRunDateTime && DateTimeService.toUiDateTime(lastRunDateTime)) || 'never';
        const nextRun = (nextRunDateTime && DateTimeService.toUiDateTime(nextRunDateTime)) || 'never';
        return (
            <div className="job-info-details">
                Last run: {lastRun} | Next run: {nextRun}
            </div>
        );
    }

    private _renderParametersCard() {
        const { isJobSettings } = this.props;
        const { isParametersOpen } = this._store;

        return (
            <Card className="job-main-parameters">
                <CardTitle onClick={this._store.toggle}>
                    {isParametersOpen ? <Icon name="caret-down" /> : <Icon name="caret-right" />}
                    <span className="ml-2">{`${isParametersOpen ? 'Hide' : 'Show'} ${isJobSettings ? 'Settings' : 'Main'} Parameters`}</span>
                </CardTitle>
                {isParametersOpen && (
                    <CardBody>
                        {isJobSettings ? this._renderJobSettingsParameters() : this._renderMainParameters()}
                    </CardBody>
                )}
            </Card>
        );
    }

    private _renderJobSettingsParameters() {
        return (
            <FormGroup row>
                <Col sm={4}>Instance</Col>
                <Col sm={8}>
                    <FormSimpleSelect formModel={this._store.form} name="instance" options={JOB_INSTANCES} hasNoEmptyOption disabled={!this._isEditable} />
                </Col>
            </FormGroup>
        );
    }

    private _renderMainParameters() {
        const { validityMessage, form } = this._store;

        return (
            <>
                <FormGroup row>
                    <Col sm={4}>Active</Col>
                    <Col sm={8}>
                        <FormSwitcher formModel={form} name="isActive" checked={false} disabled={!this._isEditable} />
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Col sm={3}>Use Cron</Col>
                    <Col sm={1}>
                        <Input checked={form.needUseCron} type="radio" name="radioChooseAlternative" id="needUseCron_0" onChange={this._onChangeUseCron} disabled={!this._isEditable} />{' '}
                    </Col>
                    <Col sm={8}>
                        <FormInput
                            disabled={!form.needUseCron || !this._isEditable}
                            invalid={!!validityMessage.cronField.errorMessage}
                            formModel={form}
                            name="cronSchedule"
                            bsSize="sm"
                            title={validityMessage.cronField.explanation}
                            changeHandler={this._onChangeCronSchedule}
                        />
                        {form.needUseCron && !!validityMessage.cronField.errorMessage && (
                            <div className="job-validation-error">{validityMessage.cronField.errorMessage}</div>
                        )}
                    </Col>
                </FormGroup>
                {form.needUseCron && (
                    <FormGroup row>
                        <Col sm={3}>{this._renderCronSamples()}</Col>
                        <Col sm={1} />
                        <Col sm={8}>
                            {this._renderUseCronExplanation()}
                        </Col>
                    </FormGroup>
                )}
                <FormGroup row>
                    <Col sm={3}>Start Once</Col>
                    <Col sm={1}>
                        <Input checked={!form.needUseCron} type="radio" name="radioChooseAlternative" id="needUseCron_1" onChange={this._onChangeUseCron} disabled={!this._isEditable} />{' '}
                    </Col>
                    <Col sm={8}>
                        <DatePickerValidatedControl disabled={form.needUseCron || !this._isEditable} pickerMode="datetime" formModel={form} name="dateTimeSchedule" />
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Col sm={4}>UseZrhTimeZone</Col>
                    <Col sm={8}>
                        <FormSwitcher formModel={form} name="useZrhTimeZone" checked={false} disabled={!this._isEditable} />
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Col sm={4}>MisfireLogic</Col>
                    <Col sm={8}>
                        <FormSimpleSelect formModel={form} name="misfireLogic" hasNoEmptyOption options={JOB_MISFIRE_INSTRUCTIONS} disabled={!this._isEditable} />
                    </Col>
                </FormGroup>
            </>
        );
    }

    private _renderCronSamples() {
        return (
            <div className="cron-samples">
                <div className="cron-samples-label">Samples:</div>
                <div className="cron-samples-list">
                    {CRON_SAMPLES.map(([label, expression]) => (
                        <Button
                            key={expression}
                            outline
                            size="sm"
                            className="cron-sample"
                            title={expression}
                            disabled={!this._isEditable}
                            onClick={() => this._applyCronSample(expression)}
                        >
                            {label}
                        </Button>
                    ))}
                </div>
            </div>
        );
    }

    private _renderUseCronExplanation() {
        return (
            <div className="cron-explanation">
                <div className="cron-explanation-legends">
                    {this._renderCronLegend(CRON_FIELDS)}
                    {this._renderCronLegend(CRON_SPECIAL_CHARS)}
                </div>
                <div className="cron-explanation-sample">
                    E.g:<b> &quot;0 30 10-13 * * WED,FRI&quot;</b> - trigger fires every half hour (at 10:30, 11:30, 12:30, and 13:30), on every Wednesday and Friday.
                </div>
            </div>
        );
    }

    private _renderCronLegend(items: Array<[string, string]>) {
        return (
            <div className="cron-explanation-legend">
                {items.map(([term, value]) => (
                    <React.Fragment key={term}>
                        <span className="cron-explanation-term">{term}</span>
                        <span>{value}</span>
                    </React.Fragment>
                ))}
            </div>
        );
    }

    @action.bound
    private _onChangeUseCron(event: React.ChangeEvent<HTMLInputElement>) {
        const id = (event.target as HTMLInputElement).id;
        const idParts = id.split('_');
        if (idParts.length !== 2) {
            throw new Error('Invalid element id=' + id);
        }
        const radioNumber = +id.split('_')[1];
        this._store.form.needUseCron = !radioNumber;
    }

    @action.bound
    private _onChangeCronSchedule(event: React.ChangeEvent<HTMLInputElement>) {
        const newValue = event.target.value;
        this._store.form.cronSchedule = newValue;

        extensions.executeTimeout(this._store.checkCronScheduleValidity, 100, this, newValue);
    }

    @action.bound
    private _applyCronSample(expression: string) {
        this._store.form.cronSchedule = expression;
        void this._store.checkCronScheduleValidity(expression);
    }

    @action.bound
    private _enableDefaultValueOverrule(jobParameter: JobParameterDescriptionModel) {
        jobParameter.isOverrule = true;
        this.forceUpdate();
    }

    @action.bound
    private async _disableDefaultValueOverrule(jobParameter: JobParameterDescriptionModel) {
        if (!jobParameter.jobValueRaw) return;

        const formKey = jobParameter.name as keyof FormModel;
        const currentValue = this._store.form.getValue(formKey as unknown as keyof BaseFormModel);

        let value: unknown;
        switch (jobParameter.type) {
            case JobParameterType.Date:
                value = DateTimeService.toUiDate(DateTimeService.parse(jobParameter.jobValueRaw, DateTime.jsonDateFormat));
                break;
            default:
                value = jobParameter.jobValueRaw;
                break;
        }

        if (currentValue !== jobParameter.jobValueRaw) {
            const isConfirmed = await modalService.showConfirmation('It will overwrite current value.');
            if (!isConfirmed) return;
        }

        this._store.form.setValue(formKey as unknown as keyof BaseFormModel, value as boolean | string | Date);

        jobParameter.isOverrule = false;
        this.forceUpdate();
    }
}

class JobParametersDialogStore {
    @observable isParametersOpen: boolean = false;
    @observable validityMessage: FieldsValidityMessage = { triggerNameField: '', cronField: { errorMessage: '', explanation: '' } };
    jobTriggerPrefix: string = '';
    data: JobDetailsModel;
    parameters?: JobParameterDescriptionModel[];
    form: FormModel;
    trigger: JobTriggerShortInfo | null;
    window: ModalWindow<void> | null = null;
    loader: PromiseCompletion = new PromiseCompletion();
    _showTriggerState: boolean;
    _onSubmit: OnSubmitHandler;

    constructor(showTriggerState: boolean, jobData: JobDetailsModel, onSubmit: OnSubmitHandler, trigger?: JobTriggerShortInfo | null) {
        this.data = jobData;
        this.jobTriggerPrefix = this.data.name + '_';
        this.parameters = jobData.parameters;
        this.form = new FormModel(this.parameters);
        this.trigger = trigger || null;
        this._showTriggerState = showTriggerState;
        this._onSubmit = onSubmit;
        this._initFormValues();
        this._initMainParameters();
        this._preloadCustomDropdownOptions();
    }

    // Starts the option requests before the first paint so a resolved display name is shown without
    // the "Loading options..." step. RemoteSuggestInput also loads on mount, so this is only a head start.
    private _preloadCustomDropdownOptions() {
        this.parameters?.forEach(jobParameter => {
            if (jobParameter.type !== JobParameterType.CustomDropdown || !jobParameter.optionsUrl) return;

            // The current key goes along: a searchable endpoint cannot be listed, so it is the only
            // way it can resolve the stored value to a display name.
            const currentValue = (this.form.getValue(jobParameter.name as keyof BaseFormModel) ?? '') as string;
            void RemoteOptionsStore.get(jobParameter.optionsUrl).load(currentValue);
        });
    }

    @action
    public async submit(type: ModalButtonType, isJobSettings?: boolean) {
        if (!this.form.validate() && type !== ModalButtonType.Delete) {
            const invalidFields = this.form.getInvalidFields<FormModel>();
            const isInvalidFieldFromParameters = invalidFields.includes('cronSchedule') || invalidFields.includes('dateTimeSchedule');
            if (isInvalidFieldFromParameters && !this.isParametersOpen) this.toggle();
            return;
        }

        const jobParams: Array<JobParameterModel> = [];
        this.parameters?.forEach((jobParameter: JobParameterDescriptionModel) => {
            const name = jobParameter.name;
            let value = this.form[name ?? ''] as string;
            switch (jobParameter.type) {
                case ParameterType.DateTime: {
                    value = value && DateTimeService.format(DateTimeService.parseUiDateTime(value), DateTime.jsonFullFormat);
                    break;
                }
                case ParameterType.Date: {
                    value = value && DateTimeService.format(DateTimeService.parseUiDate(value), DateTime.jsonDateFormat);
                    break;
                }
            }

            let shouldIncludeParameter = false;
            if (isJobSettings) {
                shouldIncludeParameter = !!value || jobParameter.type === ParameterType.Boolean;
            } else {
                shouldIncludeParameter = typeof jobParameter.isOverrule === 'boolean'
                    || !!value || jobParameter.type === ParameterType.Boolean;
            }

            if (shouldIncludeParameter) {
                let valueToPush: string | undefined = value ? value + '' : undefined;
                if (typeof jobParameter.isOverrule === 'boolean' && jobParameter.type === ParameterType.Boolean) {
                    valueToPush = value ? 'true' : 'false';
                }
                jobParams.push(new JobParameterModel({ name: name || '', value: valueToPush }));
            }
        });

        const dateTimeSchedule = (this.form.dateTimeSchedule && DateTimeService.parseUiDateTime(this.form.dateTimeSchedule)) || null;
        if (type === ModalButtonType.RunNow || type === ModalButtonType.SaveAndRunNow) {
            const runTriggerName = (this.trigger && this.trigger.name) || this.data.name || '';
            jobParams.push(new JobParameterModel({ name: 'RunTriggerName', value: runTriggerName }));
        }
        const jobDetails: JobParametersSubmitModel = new JobSaveParametersModel({
            jobName: this.data.name ?? '',
            parameters: jobParams,
            triggerName: (this.trigger && this.trigger.name) || this.jobTriggerPrefix + this.form.triggerName,
            isActive: this.form.isActive,
            cronSchedule: this.form.needUseCron ? this.form.cronSchedule : '',
            dateTimeSchedule: (!this.form.needUseCron && dateTimeSchedule) || undefined,
            useZrhTimeZone: this.form.useZrhTimeZone,
            misfireLogic: this.form.misfireLogic
        });
        jobDetails.isJobSettings = !!isJobSettings;
        if (isJobSettings) {
            jobDetails.instance = this.form.instance || undefined;
        }

        const result = await this.loader.add(async () => {
            return await this._onSubmit(type, jobDetails);
        });

        if (result) {
            this.window?.close();
        }
        return result;
    }

    private _initFormValues() {
        this.parameters?.map((jobParameter: JobParameterDescriptionModel) => {
            const hasNoValuesToSet = !jobParameter.valueRaw && !jobParameter.jobValueRaw;
            const hasNoOverruleValueToSet = !jobParameter.valueRaw && jobParameter.isOverrule;

            if (hasNoValuesToSet || hasNoOverruleValueToSet) {
                return;
            }

            const prop = jobParameter.name as keyof FormModel;

            let valueToSet = jobParameter.valueRaw ?? jobParameter.jobValueRaw;

            if (!valueToSet) {
                return;
            }

            if (jobParameter.type === JobParameterType.DateTime) {
                const date = DateTimeService.parse(valueToSet, DateTime.jsonDateTimeFormat);
                if (!isNaN(date.getTime())) {
                    valueToSet = DateTimeService.toUiDate(date);
                }
            }

            if (jobParameter.type === JobParameterType.Date) {
                const date = DateTimeService.parse(valueToSet, DateTime.jsonDateFormat);
                if (!isNaN(date.getTime())) {
                    valueToSet = DateTimeService.toUiDate(date);
                }
            }

            this.form[prop] = valueToSet;
        });
    }

    private _initMainParameters() {
        let date = '';
        if (this.trigger) {
            date = this.trigger.dateTimeSchedule ? DateTimeService.toUiDateTime(this.trigger.dateTimeSchedule) : '';
            this.form.isActive = this.trigger.isActive;
            this.form.useZrhTimeZone = this.trigger.useZrhTimeZone;
            this.form.misfireLogic = this.trigger.misfireLogic;
            this.form.cronSchedule = this.trigger.cronSchedule || '';
            void this.checkCronScheduleValidity(this.form.cronSchedule);
        } else {
            date = this.data.dateTimeSchedule ? DateTimeService.toUiDateTime(this.data.dateTimeSchedule) : '';
            this.form.cronSchedule = this.data.cronSchedule || '';
        }
        if (this._showTriggerState) {
            this.form.needUseCron = !!this.form.cronSchedule;
        }
        this.form.instance = this.data.instance;
        this.form.dateTimeSchedule = this.form.needUseCron ? '' : date;

        if (!this.trigger && this._showTriggerState) {
            addRequiredValidator(this.form, 'triggerName', '', 'Name of trigger');
        }
    }

    public checkCronScheduleValidity = async (cronExpression: string) => {
        if (!cronExpression) return;

        const url = ApiUrls.JobCronValidation + `/${encodeURI(cronExpression).replace(/\?/g, '%3F')}`;
        const { data } = await ApiService.post<CronValidationResult>(url);

        runInAction(() => {
            this.validityMessage.cronField.errorMessage = data.validationErrorMessage ?? '';
            this.validityMessage.cronField.explanation = data.expressionSummary ?? '';
        });
    };

    @action.bound
    public toggle() {
        this.isParametersOpen = !this.isParametersOpen;
    }
}
